/**
 * Care Background Agent · frontend/src/hooks/useSession.ts
 * Session data + real-time WebSocket streaming from Durable Object.
 */

import { useState, useEffect, useRef, useCallback } from "react";

const API = import.meta.env.VITE_WORKER_URL ?? "";
const CARE_SECRET = import.meta.env.VITE_CARE_SECRET ?? "";
const WS_URL = API.replace(/^https?/, API.startsWith("https") ? "wss" : "ws");

export interface Message {
  id: string;
  session_id: string;
  role: "user" | "assistant" | "tool" | "system";
  sender_name?: string;
  sender_email?: string;
  content: string;
  ts: string;
}

export interface Session {
  id: string;
  thread_id: string;
  status: string;
  task_title?: string;
  code_server_url?: string;
  vnc_url?: string;
  sandbox_provider?: string;
  initiating_email?: string;
  initiating_name?: string;
  pr_url?: string;
  handover_report?: string;
  summary_json?: string;
  environment?: string;
  created_at: string;
}

export interface LiveEvent {
  type: string;
  [key: string]: unknown;
}

interface UseSessionReturn {
  session: Session | null;
  messages: Message[];
  liveEvents: LiveEvent[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const MAX_LIVE_EVENTS = 200;
const WS_RECONNECT_DELAY = 3000;

export function useSession(sessionId: string): UseSessionReturn {
  const [session, setSession] = useState<Session | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [liveEvents, setLiveEvents] = useState<LiveEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  const headers = { "X-Care-Secret": CARE_SECRET };

  const fetchSession = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/sessions/${encodeURIComponent(sessionId)}`, { headers });
      if (!res.ok) throw new Error(`Session ${res.status}`);
      const data = await res.json();
      if (mountedRef.current) setSession(data);
    } catch (e) {
      if (mountedRef.current) setError(String(e));
    }
  }, [sessionId]);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/sessions/${encodeURIComponent(sessionId)}/messages`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      if (mountedRef.current) setMessages(data.messages ?? []);
    } catch {}
  }, [sessionId]);

  const connectWS = useCallback(() => {
    if (!mountedRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(
      `${WS_URL}/api/sessions/${encodeURIComponent(sessionId)}/live`
    );
    wsRef.current = ws;

    ws.onopen = () => {
      console.debug("[care-ws] connected for session", sessionId);
    };

    ws.onmessage = (e) => {
      try {
        const event: LiveEvent = JSON.parse(e.data);
        if (!mountedRef.current) return;

        setLiveEvents((prev) => {
          const next = [...prev, event];
          return next.length > MAX_LIVE_EVENTS ? next.slice(-MAX_LIVE_EVENTS) : next;
        });

        if (event.type === "message" && (event as { role?: string }).role === "assistant") {
          fetchMessages();
          fetchSession();
        }
        if (event.type === "session_update") {
          fetchSession();
        }
      } catch {}
    };

    ws.onerror = () => {
      console.debug("[care-ws] error, will reconnect");
    };

    ws.onclose = () => {
      if (mountedRef.current) {
        reconnectTimerRef.current = setTimeout(connectWS, WS_RECONNECT_DELAY);
      }
    };
  }, [sessionId, fetchMessages, fetchSession]);

  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);
    setError(null);

    Promise.all([fetchSession(), fetchMessages()]).finally(() => {
      if (mountedRef.current) setLoading(false);
    });

    connectWS();

    const pollTimer = setInterval(fetchSession, 30000);

    return () => {
      mountedRef.current = false;
      clearInterval(pollTimer);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close();
    };
  }, [sessionId]);

  const refetch = useCallback(() => {
    fetchSession();
    fetchMessages();
  }, [fetchSession, fetchMessages]);

  return { session, messages, liveEvents, loading, error, refetch };
}
